import type { Currency } from "@/lib/domain/Currency";
import type { Money } from "@/lib/domain/Money";

export type MoneyDisplayFormat = "Standard" | "Extended" | "Colored" | "Delta" | "DeltaPercentage";

export const buildDisplayOutput = (money: Money, preferredCurrency: Currency, mode: MoneyDisplayFormat, originalValue?: number, className?: string): { text: string; className: string | undefined } => {
	const positiveMoney = { value: Math.abs(money.value), currency: money.currency };
	const prefix = getPrefixForCurrency(money.currency);
	const formattedValue = formatCurrencyString(positiveMoney, mode);

	let text = money.currency !== preferredCurrency
		? prefix + formattedValue
		: formattedValue;

	if (money.value < 0) {
		text = "-" + text;
	}

	return {
		text: applyDeltaFormatting(text, mode, money.value, originalValue),
		className: getDisplayClassName(mode, money.value, className)
	};
};

const getPrefixForCurrency = (currency: Currency): string => {
	switch (currency) {
	case "AUD":
		return "AU";
	case "NZD":
		return "NZ";
	case "CAD":
		return "CA";
	case "SGD":
		return "SG";
	case "USD":
		return "US";
	default:
		return "";
	}
};

const formatPercentage = (value: number): string => {
	return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
};

const applyDeltaFormatting = (text: string, mode: MoneyDisplayFormat, moneyValue: number, originalValue?: number): string => {
	if (mode === "DeltaPercentage") {
		if (originalValue) {
			const percentageValue = moneyValue / originalValue * 100;
			return moneyValue > 0
				? `+${text} (${formatPercentage(percentageValue)})`
				: `${text} (${formatPercentage(percentageValue)})`;
		}

		return moneyValue > 0 ? "+" + text : text;
	}

	if (mode === "Delta") {
		return moneyValue > 0 ? "+" + text : text;
	}

	return text;
};

const getDisplayClassName = (mode: MoneyDisplayFormat, value: number, className?: string): string | undefined => {
	if (mode === "Colored" || mode === "Delta" || mode === "DeltaPercentage") {
		return getClassNamesForValue(value, className);
	}
	return className;
};

const getClassNamesForValue = (value: number, className: string | undefined): string => {
	const classNames: string[] = [];
	if (className) {
		classNames.push(className);
	}

	if (value > 0) {
		classNames.push("text-primary");
	} else if (value < 0) {
		classNames.push("text-destructive");
	} else {
		classNames.push("text-foreground");
	}

	return classNames.join(" ");
};

const formatCurrencyString = (money: Money, mode: MoneyDisplayFormat): string => {
	return money.value.toLocaleString(undefined, {
		style: "currency",
		currency: money.currency,
		currencyDisplay: "narrowSymbol",
		maximumFractionDigits: mode === "Extended" ? 5
			: money.currency === "IDR" || money.currency === "JPY" ? 0 : undefined
	});
};
